/**
 * Суфлёр (§6.4).
 *
 * Стоит вверху экрана, у самой камеры, и показывает одну фразу крупно.
 * Так человек смотрит почти в объектив: раньше текст жил под видоискателем,
 * и на готовом ролике было видно, что человек читает куда-то вниз и вбок —
 * фотоаватар оживляет именно такой кадр, и взгляд из него никуда не девается.
 *
 * Скорость — по длине блоков, а не поровну (см. `scriptBlockShares`):
 * на короткой фразе человек больше не сидит молча, ожидая, пока текст
 * поедет дальше.
 *
 * Следующая фраза видна бледной строкой: без неё переход всегда неожиданный,
 * и на стыке возникает пауза.
 */
import clsx from 'clsx';
import { scriptBlockAt, type OnboardingScript } from '@alterium/shared';

interface TeleprompterProps {
  script: OnboardingScript;
  /** Доля прочитанного 0-1: по ней выбирается текущий блок. */
  progress: number;
  active: boolean;
}

export function Teleprompter({ script, progress, active }: TeleprompterProps) {
  const index = active ? scriptBlockAt(script, progress) : 0;
  const block = script.blocks[index];
  const next = script.blocks[index + 1];
  if (!block) return null;

  return (
    <div className="flex flex-col items-center gap-2 px-5 text-center">
      <p className="text-[13px] uppercase tracking-wide text-accent">{block.hint}</p>

      <p className="max-w-[34rem] text-[22px] font-medium leading-snug text-content">
        {block.text}
      </p>

      {next ? (
        <p
          className={clsx(
            'max-w-[34rem] text-[15px] leading-snug text-content-muted',
            active ? 'opacity-60' : 'opacity-40',
          )}
        >
          {next.text}
        </p>
      ) : null}

      {/* Точки вместо полосы: сколько фраз пройдено и сколько осталось. */}
      <div className="flex gap-1.5 pt-1">
        {script.blocks.map((item, position) => (
          <span
            key={item.text}
            className={clsx(
              'h-1.5 w-1.5 rounded-full transition-colors duration-slow',
              position < index ? 'bg-accent/50' : position === index ? 'bg-accent' : 'bg-line',
            )}
          />
        ))}
      </div>
    </div>
  );
}
